import { useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { initialPersonaManagerState, personaManagerReducer } from './personaManagerReducer';
import { useCreateCastRelationship } from '../../../domains/scene/ui/hooks/useCreateCastRelationship';
import { useGetUserInfo } from '../../../domains/user/ui/hooks/useGetUserInfo';
import { useWarningToast } from '../../hooks/useWarningToast';
import { buildCastRelationshipBodyFromPersonas } from '../utils/buildCastRelationshipBody';
import { updatePendingPersona } from '../utils/updatePendingPersona';
import saveData from '../../../utils/saveData';
import type {
  SceneArtistSelectionResult,
  ScenePersona,
  ScenePersonaSlot,
  SceneSettingStep
} from '../types';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import { ROUTES } from '../../../constants/routes';

interface UsePersonaManagerParams {
  closeBottomSheet: () => void;
}

export type PersonaSearchResult =
  | { kind: 'artist'; persona: ScenePersona }
  | { kind: 'celeb'; persona: Pick<ScenePersona, 'nickname'> & { dataUrl?: string } };

export const usePersonaManager = ({ closeBottomSheet }: UsePersonaManagerParams) => {
  const [state, dispatch] = useReducer(personaManagerReducer, initialPersonaManagerState);
  const { firstPersona, secondPersona, pendingPersona, step, activePersonaSlot } = state;
  const showWarningToast = useWarningToast();

  const { mutateAsync: createCastRelationship } = useCreateCastRelationship();
  const { data: user } = useGetUserInfo();
  const navigate = useNavigate();

  const initialPersona =
    activePersonaSlot === 'first'
      ? firstPersona
      : activePersonaSlot === 'second'
        ? secondPersona
        : null;

  const canProceedNext = Boolean(firstPersona) && Boolean(secondPersona);

  const resolvePersonaWithUserId = (
    persona: ScenePersona,
    currentUserId: string | undefined
  ): ScenePersona =>
    persona.entityType === 'User' && !persona.entityId && currentUserId
      ? { ...persona, entityId: currentUserId }
      : persona;

  const handleCreateCastRelationship = async (personas: {
    first: ScenePersona;
    second: ScenePersona;
  }) => {
    const body = buildCastRelationshipBodyFromPersonas(personas);
    return await createCastRelationship(body);
  };

  const clearPendingPersona = () => dispatch({ type: 'CLEAR_PENDING' });
  const resetPersonas = () => dispatch({ type: 'RESET_PERSONAS' });
  const setStep = (nextStep: SceneSettingStep) => dispatch({ type: 'SET_STEP', payload: nextStep });
  const setActivePersonaSlot = (slot: ScenePersonaSlot | null) =>
    dispatch({ type: 'SET_ACTIVE_PERSONA_SLOT', payload: slot });
  const cancelPersonaFlow = (backTo: SceneSettingStep) =>
    dispatch({ type: 'CANCEL_PERSONA_FLOW', payload: backTo });
  const goBackFromSecond = () => dispatch({ type: 'GO_BACK_FROM_SECOND' });

  const commitPendingPersonaAndAdvance = (persona: ScenePersona, slot: ScenePersonaSlot) => {
    dispatch({ type: 'COMMIT_PENDING_AND_ADVANCE', payload: { persona, slot } });
  };

  type PendingPatch = Parameters<typeof updatePendingPersona>[1];
  const applyPendingInfoAndCommit = (partial: PendingPatch) => {
    const next = updatePendingPersona(pendingPersona, partial);
    if (next && activePersonaSlot) {
      dispatch({
        type: 'COMMIT_PENDING_AND_ADVANCE',
        payload: { persona: next, slot: activePersonaSlot }
      });
    }
  };

  const handleSelection = (result: SceneArtistSelectionResult) => {
    closeBottomSheet();
    if (!activePersonaSlot) {
      dispatch({ type: 'GO_TO_SELECT' });
      return;
    }
    if (result.kind === 'user') {
      dispatch({ type: 'SELECT_USER_PERSONA', payload: { persona: result.persona } });
      return;
    }
    if (result.kind === 'celeb') {
      dispatch({ type: 'SELECT_CELEB_PERSONA', payload: { persona: result.persona } });
      return;
    }
    const persona: ScenePersona = {
      ...result.persona,
      entityType: 'Member',
      entityId: result.persona.memberId
    };
    commitPendingPersonaAndAdvance(persona, activePersonaSlot);
  };

  const handleEnterArtistInfoSubmit = (partial: {
    occupationType?: ScenePersona['occupationType'];
    occupationDescription?: string;
  }) => applyPendingInfoAndCommit(partial);

  const handleEnterUserInfoSubmit = (partial: {
    personality?: string;
    ageGapType?: ScenePersona['ageGapType'];
    gender?: ScenePersona['gender'];
    nickname?: string;
    dataUrl?: string;
  }) => applyPendingInfoAndCommit(partial);

  const userNickname = user?.baseUserProfile?.nickname ?? '';

  const handleSetSlotAsUser = (slot: ScenePersonaSlot) => {
    dispatch({ type: 'START_USER_FLOW', payload: { slot, userNickname } });
  };

  const handlePersonaFromSearchBySlot = (result: PersonaSearchResult, slot: ScenePersonaSlot) => {
    if (result.kind === 'artist') {
      const persona: ScenePersona = {
        ...result.persona,
        dataUrl: result.persona.dataUrl,
        entityType: 'Member',
        entityId: result.persona.memberId
      };
      commitPendingPersonaAndAdvance(persona, slot);
    } else {
      dispatch({
        type: 'START_CELEB_FLOW',
        payload: {
          slot,
          persona: { nickname: result.persona.nickname, dataUrl: result.persona.dataUrl }
        }
      });
    }
  };

  const setPersonaDataUrl = (slot: ScenePersonaSlot, dataUrl: string) => {
    dispatch({ type: 'SET_PERSONA_DATA_URL', payload: { slot, dataUrl } });
  };

  const handleNext = async () => {
    if (!firstPersona || !secondPersona) return;
    const currentUserId = user?.baseUserProfile?.userId;
    const first = resolvePersonaWithUserId(firstPersona, currentUserId);
    const second = resolvePersonaWithUserId(secondPersona, currentUserId);

    try {
      const response = await handleCreateCastRelationship({ first, second });

      if (response?.castRelationshipId) {
        saveData(LOCAL_STORAGE_KEYS.CURRENT_CAST_RELATIONSHIP_ID, response.castRelationshipId);
        dispatch({ type: 'SET_STEP', payload: 'loadingScene' });
      } else {
        navigate(ROUTES.SCENE.SETTING);
      }
    } catch (error) {
      if ((error as { error?: { code?: string } }).error?.code === 'InsufficientTicket') {
        showWarningToast('bottom', '인물 조합 변경권이 부족합니다.', true);
      }
      dispatch({ type: 'SET_STEP', payload: 'select' });
    }
  };

  return {
    state: {
      firstPersona,
      secondPersona,
      pendingPersona,
      initialPersona,
      step,
      activePersonaSlot,
      canProceedNext
    },
    actions: {
      clearPendingPersona,
      resetPersonas,
      setStep,
      setActivePersonaSlot,
      cancelPersonaFlow,
      goBackFromSecond,
      handleSelection,
      handleEnterArtistInfoSubmit,
      handleEnterUserInfoSubmit,
      handleNext,
      handleSetSlotAsUser,
      handlePersonaFromSearchBySlot,
      setPersonaDataUrl
    }
  };
};
